import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, withStyles } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import CloseIcon from "@material-ui/icons/Close";
import ContactsIcon from "@material-ui/icons/Contacts";
import EventIcon from "@material-ui/icons/Event";
import PaymentIcon from "@material-ui/icons/Payment";
import SendIcon from "@material-ui/icons/Send";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import axios from "axios";
import { STRIPE_BASE_URL } from "../../utils/constants";
import trackEvent from "../../utils/appMetrics";

// Setup progress as a corner pill instead of a card on Home.
//
// The card version was the most prominent thing on the dashboard and owners
// still walked past it, so more prominence was never the fix. The work happens
// on Contacts and Classes, not Home, and this rides along to those pages: a
// small pill that says how far along you are, expands on click into the list
// with deep links, and disappears for good once you're done. Pages carry their
// own contextual prompts (see ClassSetupProgress); this is only the "where am I
// overall" anchor.

const postChecklistState = (path) => {
  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    axios.post(path, {}, { headers: { "X-CSRF-Token": csrfToken } });
  } catch (e) {
    // Non-fatal: the localStorage flag still applies on this device.
  }
};

const reduceMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Panel = withStyles({
  "@keyframes swyvelPanelIn": {
    "0%": { opacity: 0, transform: "translateY(8px) scale(.97)" },
    "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
  },
  root: {
    width: 330,
    maxWidth: "calc(100vw - 40px)",
    background: "#ffffff",
    border: "1px solid #E0E0E0",
    borderRadius: 16,
    boxShadow: "0 18px 40px rgba(0,0,0,.16)",
    padding: 18,
    marginBottom: 10,
    transformOrigin: "bottom left",
    animation: "$swyvelPanelIn .22s cubic-bezier(.22,.9,.36,1)",
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
})(Box);

const TaskRow = withStyles({
  root: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "9px 10px",
    borderRadius: 10,
    transition: "background-color .15s ease",
  },
  actionable: {
    cursor: "pointer",
    "&:hover": { backgroundColor: "#F5F8FC" },
  },
})(({ classes, actionable, ...rest }) => (
  <Box className={`${classes.root}${actionable ? ` ${classes.actionable}` : ""}`} {...rest} />
));

// Ring rather than a bar: at pill size a bar reads as decoration, a ring reads
// as a dial with somewhere left to go.
const ProgressRing = ({ done, total, size = 26 }) => {
  const stroke = 3;
  const radius = (size - stroke) / 2;
  const c = size / 2;
  const pct = total === 0 ? 0 : Math.min(1, done / total);
  // Explicit arc from 12 o'clock sweeping clockwise (sweep-flag 1), the
  // progress-ring standard (clocks, Apple rings, Material). Built as a path
  // so the direction is guaranteed by geometry, not dash-offset arithmetic.
  const theta = 2 * Math.PI * pct;
  const endX = c + radius * Math.sin(theta);
  const endY = c - radius * Math.cos(theta);
  const largeArc = pct > 0.5 ? 1 : 0;
  return (
    <svg width={size} height={size} style={{ display: "block", flexShrink: 0 }}>
      <circle cx={c} cy={c} r={radius} fill="none" stroke="#EEF0F2" strokeWidth={stroke} />
      {pct >= 1 ? (
        <circle cx={c} cy={c} r={radius} fill="none" stroke="#007BFF" strokeWidth={stroke} />
      ) : pct > 0 ? (
        <path
          d={`M ${c} ${c - radius} A ${radius} ${radius} 0 ${largeArc} 1 ${endX} ${endY}`}
          fill="none"
          stroke="#007BFF"
          strokeWidth={stroke}
          strokeLinecap="round"
        />
      ) : null}
    </svg>
  );
};

const SetupPill = ({ onScheduleLesson }) => {
  const studio = typeof window !== "undefined" ? window.studio_checklist : null;
  const currentUser = (typeof window !== "undefined" && window.current_user) || {};

  const [dismissed, setDismissed] = useState(true);
  const [open, setOpen] = useState(false);
  const [flash, setFlash] = useState(false);
  const [ringing, setRinging] = useState(false);
  const [stripeAsk, setStripeAsk] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!studio?.id) return;

    // "Setup guide" in the profile menu links to /?setup_guide=1. That's an
    // explicit request for this, wherever it was dismissed.
    if (new URLSearchParams(window.location.search).get("setup_guide")) {
      try {
        localStorage.removeItem(`checklist_dismissed_${studio.id}`);
      } catch (e) {
        // Private mode: the server flag below still restores it.
      }
      postChecklistState("/onboarding_checklist/restore");
      setDismissed(false);
      setOpen(true);
      setFlash(true);
      setTimeout(() => setFlash(false), 1800);
      return;
    }

    let locallyDismissed = false;
    try {
      locallyDismissed = localStorage.getItem(`checklist_dismissed_${studio.id}`) === "true";
    } catch (e) {
      locallyDismissed = false;
    }
    const isDismissed = locallyDismissed || !!studio.dismissed;
    setDismissed(isDismissed);

    // Open itself once, the first time an owner ever sees it, so it's found.
    // After that it stays out of the way until asked for.
    if (!isDismissed) {
      try {
        if (!localStorage.getItem(`setup_pill_seen_${studio.id}`)) {
          localStorage.setItem(`setup_pill_seen_${studio.id}`, "1");
          setOpen(true);
        }
      } catch (e) {
        // Private mode: stays collapsed, which is the safe default.
      }
    }
  }, [studio?.id]);

  // Clicking anywhere else closes it, the way a menu should behave.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const dismiss = () => {
    try {
      localStorage.setItem(`checklist_dismissed_${studio.id}`, "true");
    } catch (e) {
      // Server flag below still carries it across devices.
    }
    postChecklistState("/onboarding_checklist/dismiss");
    setOpen(false);
    setDismissed(true);
  };

  // Onboarding asked how the studio runs, so only show the half that applies.
  const primaryFormat = studio?.primary_format || "both";
  const runsClasses = primaryFormat === "group" || primaryFormat === "both";
  const runsPrivate = primaryFormat === "private" || primaryFormat === "both";
  const stripeUrl = `${STRIPE_BASE_URL}?response_type=code&client_id=${window.STRIPE_CLIENT_ID}&scope=read_write&state=${currentUser.manage_studio_id}`;

  // Stripe's own sign-up asks for a legal name, a tax ID and a bank account.
  // Met in the first minute, before anything in Swyvel has worked for them,
  // it is where new owners left: three of the four who pressed "Connect
  // payments" in their first minutes (Aug to Oct 2026) never came back from
  // that form. So payments is a later step. Until something real is in the
  // studio it says so, and a press explains what Stripe will ask instead of
  // leaving for it. Anyone who wants it now can still have it.
  const startedForReal =
    studio?.leads_count > 0 || studio?.students_count > 0 || studio?.classes_count > 0 || studio?.lessons_count > 0;
  const openStripe = () => {
    // Fire and forget so the new tab opens inside the click gesture.
    trackEvent("stripe_onboarding_started", { source: "setup_pill", studio_id: studio?.id, started_for_real: !!startedForReal });
    window.open(stripeUrl, "_blank", "noopener");
  };

  const tasks = [
    {
      id: "add_students",
      title: "Add your students",
      hint: "Import the CSV you already have",
      icon: <ContactsIcon />,
      completed: studio?.leads_count > 0 || studio?.students_count > 0,
      action: () => (window.location.href = "/leads"),
    },
    runsClasses && {
      id: "create_class",
      title: "Create your first class",
      hint: "Enrollment, attendance, and tuition flow from it",
      icon: <EventIcon />,
      completed: studio?.classes_count > 0,
      action: () => (window.location.href = "/studio_classes?new=1"),
      tourUrl: "/studio_classes?tour=classes",
    },
    runsPrivate && {
      id: "schedule_lesson",
      title: "Schedule your first lesson",
      hint: "Pick a student and a time",
      icon: <CalendarTodayIcon />,
      completed: studio?.lessons_count > 0,
      // NavBar owns the lesson modal, so this opens it in place on any page.
      // The old fallback sent people to /lessons/new, which isn't a route.
      action: () => {
        setOpen(false);
        if (onScheduleLesson) onScheduleLesson();
        else window.location.href = "/lessons/view_calendar";
      },
    },
    {
      id: "nurture_on",
      title: "Turn on follow-ups",
      hint: "New inquiries get warm emails until they reply",
      icon: <SendIcon />,
      completed: studio?.nurture_on,
      action: () => (window.location.href = "/grow"),
    },
    {
      id: "connect_stripe",
      title: "Take payments online",
      hint: startedForReal
        ? "About five minutes on Stripe. Have your bank details and tax ID to hand."
        : "Later, once you have a student in. Until then you can record payments by hand.",
      icon: <PaymentIcon />,
      completed: studio?.stripe_connected,
      later: !startedForReal,
      action: () => {
        if (startedForReal) return openStripe();
        if (!stripeAsk) trackEvent("stripe_onboarding_explained", { source: "setup_pill", studio_id: studio?.id });
        return setStripeAsk(true);
      },
    },
  ].filter(Boolean);

  const done = tasks.filter((t) => t.completed).length;
  const total = tasks.length;
  const allDone = done === total;
  const nextId = tasks.find((t) => !t.completed)?.id;

  // Completing the last one retires it permanently, after a beat of credit.
  useEffect(() => {
    if (!studio?.id || dismissed || !allDone) return undefined;
    setRinging(true);
    const t = setTimeout(() => dismiss(), 3200);
    return () => clearTimeout(t);
  }, [allDone, dismissed, studio?.id]);

  if (!studio?.id || !currentUser.id || dismissed) return null;

  return (
    <Box
      ref={wrapRef}
      // swyvel-setup-pill: moved clear of the side menu (application.scss).
      className="swyvel-setup-pill"
      style={{
        position: "fixed",
        left: 20,
        bottom: 20,
        // Under MUI's modal layer (1300) so dialogs always cover it.
        zIndex: 1100,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        fontFamily: "'Nunito', 'Varela Round', sans-serif",
      }}
    >
      {open && (
        <Panel>
          <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <Box>
              <Typography style={{ fontSize: 15, fontWeight: 600, color: "#1a1a1a" }}>
                {allDone ? "You're all set" : "Finish setting up"}
              </Typography>
              <Typography style={{ fontSize: 12.5, color: "#666666", marginTop: 2 }}>
                {allDone ? "Nice work, this is done." : `${done} of ${total} done`}
              </Typography>
            </Box>
            <CloseIcon
              onClick={dismiss}
              aria-label="Hide setup progress"
              style={{ fontSize: 18, color: "#B0B4B9", cursor: "pointer", marginTop: 2 }}
            />
          </Box>

          <Box style={{ marginTop: 12, marginBottom: 6 }}>
            {tasks.map((task) => {
              const isNext = task.id === nextId;
              return (
                <TaskRow
                  key={task.id}
                  actionable={!task.completed}
                  onClick={task.completed ? undefined : task.action}
                >
                  <Box
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 9,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      backgroundColor: task.completed ? "transparent" : isNext ? "#E8F1FF" : "#F4F6F8",
                      color: task.completed ? "#00695F" : isNext ? "#007BFF" : "#6B7280",
                    }}
                  >
                    {task.completed ? <CheckCircleIcon style={{ fontSize: 18 }} /> : React.cloneElement(task.icon, { style: { fontSize: 16 } })}
                  </Box>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      style={{
                        fontSize: 13.5,
                        fontWeight: isNext ? 600 : 500,
                        color: task.completed ? "#6B7280" : "#1a1a1a",
                        textDecoration: task.completed ? "line-through" : "none",
                      }}
                    >
                      {task.title}
                    </Typography>
                    {!task.completed && (
                      <Typography style={{ fontSize: 12, color: "#666666", lineHeight: 1.45 }}>{task.hint}</Typography>
                    )}
                    {!task.completed && task.tourUrl && (
                      <Typography
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = task.tourUrl;
                        }}
                        style={{ display: "inline-block", marginTop: 3, fontSize: 12, fontWeight: 600, color: "#007BFF", cursor: "pointer" }}
                      >
                        Show me how (30 sec)
                      </Typography>
                    )}
                  </Box>
                  {task.completed ? null : task.later ? (
                    <Typography style={{ fontSize: 11.5, fontWeight: 600, color: "#666666", marginTop: 4, whiteSpace: "nowrap" }}>Later</Typography>
                  ) : isNext ? (
                    <ArrowForwardIcon style={{ color: "#007BFF", fontSize: 17, marginTop: 4 }} />
                  ) : (
                    <RadioButtonUncheckedIcon style={{ color: "#E0E0E0", fontSize: 17, marginTop: 4 }} />
                  )}
                </TaskRow>
              );
            })}
          </Box>
          {stripeAsk && !startedForReal && !studio?.stripe_connected && (
            <Box data-testid="stripe-later" style={{ background: "#F6F7F9", borderRadius: 12, padding: "12px 14px", marginTop: 4 }}>
              <Typography style={{ fontSize: 13, color: "#1a1a1a", lineHeight: 1.5 }}>
                Stripe will ask for your legal name, a tax ID and a bank account. It takes about five minutes, and
                nothing else in Swyvel needs it. Most studios do it once their students are in.
              </Typography>
              <Box style={{ display: "flex", gap: 16, marginTop: 8 }}>
                <Typography onClick={() => setStripeAsk(false)} style={{ fontSize: 13, fontWeight: 600, color: "#007BFF", cursor: "pointer" }}>
                  Not yet
                </Typography>
                <Typography onClick={openStripe} style={{ fontSize: 13, fontWeight: 600, color: "#666666", cursor: "pointer" }}>
                  Connect Stripe now
                </Typography>
              </Box>
            </Box>
          )}
        </Panel>
      )}

      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`Setup progress, ${done} of ${total} done`}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        onMouseEnter={(e) => {
          if (reduceMotion()) return;
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 10px 24px rgba(0,0,0,.16)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,.12)";
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          background: "#ffffff",
          border: `1px solid ${flash || ringing ? "#007BFF" : "#E0E0E0"}`,
          borderRadius: 999,
          padding: "7px 15px 7px 9px",
          cursor: "pointer",
          boxShadow: flash || ringing
            ? "0 0 0 4px rgba(0,123,255,.18), 0 6px 16px rgba(0,0,0,.12)"
            : "0 6px 16px rgba(0,0,0,.12)",
          transition: reduceMotion() ? "none" : "transform .18s cubic-bezier(.22,.9,.36,1), box-shadow .3s ease, border-color .3s ease",
        }}
      >
        <ProgressRing done={done} total={total} />
        <Typography style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a", whiteSpace: "nowrap" }}>
          {allDone ? "All set" : `Setup ${done} of ${total}`}
        </Typography>
      </Box>
    </Box>
  );
};

export default SetupPill;
